/**
 * Клиентское зеркало алгоритма «общий статус» раздела «Охрана труда» (design D2, Б6;
 * `_РЕШЕНИЯ_PM_фаза2.md`). Сервер — источник истины (`backend/internal/domain/ot_status.go`);
 * здесь — по конвенции `lib/training.ts`/`lib/sout.ts`: чистые функции для тестов и
 * согласованности клиентских бейджей, не для авторитетного решения (таблица `/ot` приходит с
 * сервера уже с готовым `overallStatus` на каждой строке — фронт его не пересчитывает).
 */
import type { StatusRole } from '@/design-system/tokens/status'
import type { OtSignal, RiskLevel, TrainingStatus } from '@/lib/api'

const SIGNAL_RANK: Record<OtSignal, number> = { ok: 0, no_data: 1, expiring: 2, overdue: 3 }

/**
 * Общий статус — худший из переданных ПРИМЕНИМЫХ сигналов (design D2): overdue > expiring >
 * no_data > ok. «ok» — только если ВСЕ переданные сигналы ok (пустой набор — вырожденный
 * случай, тоже ok — нет ни одного проблемного сигнала).
 */
export function computeOverallStatus(signals: OtSignal[]): OtSignal {
  let worst: OtSignal = 'ok'
  for (const s of signals) {
    if (SIGNAL_RANK[s] > SIGNAL_RANK[worst]) worst = s
  }
  return worst
}

/**
 * Сигнал обучения человека по списку статусов ЕГО ЗАПИСЕЙ (design D2; статус каждой записи —
 * уже посчитанный `computeTrainingStatus` из `lib/training.ts`, авторитетный пересчёт, не
 * хранимая колонка). Нет записей → no_data; есть `expired` → overdue; есть `expiring` (без
 * `expired`) → expiring; иначе (`valid`/`in_process`) → ok — `in_process` не считается
 * проблемой (согласовано с `buildEmployeeBadges`, design.md Open Questions).
 */
export function trainingSignal(statuses: TrainingStatus[]): OtSignal {
  if (statuses.length === 0) return 'no_data'
  if (statuses.includes('expired')) return 'overdue'
  if (statuses.includes('expiring')) return 'expiring'
  return 'ok'
}

/**
 * Сигнал профрисков по уровню риска результата должности (§9.2/design D2): not_assessed →
 * no_data; high → overdue; medium → expiring; acceptable → ok.
 */
export function profriskSignal(riskLevel: RiskLevel | undefined | null): OtSignal {
  switch (riskLevel) {
    case 'high':
      return 'overdue'
    case 'medium':
      return 'expiring'
    case 'acceptable':
      return 'ok'
    default:
      return 'no_data'
  }
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

/** Дата-только (локальная полночь) из строки YYYY-MM-DD — устойчиво к таймзонам. */
function parseDateOnly(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/**
 * Порог «истекает скоро» для СОУТ (design D2, Open Questions): канонического значения в
 * материалах фазы 2 нет, принят дефолт 90 дней (как у обучения), до уточнения заказчиком.
 */
export const SOUT_EXPIRING_THRESHOLD_DAYS = 90

/**
 * Сигнал СОУТ (design D2) — зеркало серверного `domain.ComputeSoutStatus`/`frontend lib/sout.ts:138`
 * (`computeSoutStatus`), но возвращает `OtSignal` (а не 4-тирный `SoutDashboardStatus` дашборда):
 * нет класса УТ → no_data; бессрочно (класс 1-2) → ok; нет срока действия → no_data; срок
 * истёк → overdue; срок в пределах порога → expiring; иначе → ok.
 */
export function computeSoutStatus(
  row: { hazardClass: string; validUntil?: string | null; perpetual: boolean },
  thresholdDays: number = SOUT_EXPIRING_THRESHOLD_DAYS,
  now: Date = new Date(),
): OtSignal {
  if (!row.hazardClass) return 'no_data'
  if (row.perpetual) return 'ok'
  if (!row.validUntil) return 'no_data'
  const vu = parseDateOnly(row.validUntil)
  if (!vu) return 'no_data'
  const today = startOfDay(now)
  const thresholdMs = today.getTime() + thresholdDays * MS_PER_DAY
  if (vu.getTime() < today.getTime()) return 'overdue'
  if (vu.getTime() <= thresholdMs) return 'expiring'
  return 'ok'
}

export interface OtStatusView {
  label: string
  role: StatusRole
}

/** Ярлык + цветовая роль общего статуса строки/карточки ОТ (§9.2, по образцу TRAINING_STATUS_VIEW/SOUT_STATUS_VIEW). */
export const OT_STATUS_VIEW: Record<OtSignal, OtStatusView> = {
  overdue: { label: 'Просрочено', role: 'danger' },
  expiring: { label: 'Истекает скоро', role: 'warning' },
  no_data: { label: 'Нет данных', role: 'neutral' },
  ok: { label: 'Всё ок', role: 'success' },
}

/**
 * F2-10 (раздельные колонки СОУТ/Профриски/Обучение с СОДЕРЖИМЫМ вместо одной колонки
 * «Статус»): форматтеры содержимого колонки «СОУТ» таблицы `/ot` — чистые функции по той же
 * конвенции файла, тестируются изолированно (ot.test.ts).
 */

/** Компактная метка «до MM.YY» (по образцу серверного termForStatus, dto_training.go). */
function shortDateLabel(iso: string): string {
  const d = parseDateOnly(iso)
  if (!d) return iso
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yy = String(d.getFullYear()).slice(-2)
  return `до ${mm}.${yy}`
}

/**
 * Текст колонки «СОУТ» строки ОТ: «‹класс› · до MM.YY» / «‹класс› · Бессрочно» / просто класс,
 * если нет срока. `null` (нет hazardClass ИЛИ сама сводка отсутствует) → «Нет данных» рисует
 * вызывающий (страница), эта функция не возвращает текст-заглушку сама.
 */
export function soutContentLabel(
  sout: { hazardClass: string; validUntil?: string | null; perpetual: boolean } | null | undefined,
): string | null {
  if (!sout || !sout.hazardClass) return null
  if (sout.perpetual) return `${sout.hazardClass} · Бессрочно`
  if (!sout.validUntil) return sout.hazardClass
  return `${sout.hazardClass} · ${shortDateLabel(sout.validUntil)}`
}
